/**
 * useOnlineLocation: This hook is used to manage the state and logic of the online location form.
 * It handles the change of the online location platform, URL, and password.
 * It submits the form and tracks the status of the form submission.
 * It stores the form data and error in the component state.
 * It also handles the error handling if any error occurs during the form submission.
 */

import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import { isEqual } from 'lodash';
import Zoom from '../../../../../../assets/images/Zoom.png';
import Meet from '../../../../../../assets/images/GoogleMeet.png';
import Discord from '../../../../../../assets/images/Discord.png';
import Youtube from '../../../../../../assets/images/youtube.png';
import Twitch from '../../../../../../assets/images/Twitch.png';
import Link from '../../../../../../assets/images/Link.png';
import NotDecided from '../../../../../../assets/svgs/platformNotDecided.svg';
import { PLATFORM_NOT_DECIDED } from '../../../../../../app/store/constant';
import { selectMenu } from '../../../../../../app/store/slices/eventDashboardMenuListSlice';
import { updateEventById } from '../../../../../../app/store/slices/event/event';
import { useEventStatus } from '../../../../../../hooks/event-hooks/useEventStatus';
import { errorToast } from '../../../../../../utils/toasts/toasts';

export default function useOnlineLocation() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.auth);
    const { eventData } = useSelector((state) => state.event);
    const { tickets } = useSelector((state) => state.ticket);
    const [isEditModeOn, setIsEditModeOn] = useState(true);
    const [openTicketPopup, setOpenTicketPopup] = useState(false);
    const [shouldShowEditButton, setShouldShowEditButton] = useState(false);
    const [isDiffMoreThan24Hours, setIsDiffMoreThan24Hours] = useState(false);
    const [loading, setLoading] = useState(false);

    const { isEventDrafted, isLocationTabCompleted, isEventLive } = useEventStatus();

    useEffect(() => {
        const shouldShow = isEventDrafted && isLocationTabCompleted;
        setShouldShowEditButton(shouldShow);

        if (eventData?.publish || isEventLive) {
            // If published → always false
            setIsEditModeOn(false);
        } else if (!isLocationTabCompleted || !isEventDrafted) {
            // If not completed OR not drafted → allow editing
            setIsEditModeOn(true);
        } else {
            // Both drafted & completed → disable edit mode
            setIsEditModeOn(false);
        }
    }, [isEventDrafted, isEventLive, isLocationTabCompleted, eventData?.publish]);

    useMemo(() => {
        if (eventData?.start_date && eventData?.start_time) {
            const eventStartDateAndTime = dayjs(
                `${eventData?.start_date} ${eventData?.start_time}`,
                'YYYY-MM-DD HH:mm'
            );
            const currentDateTime = dayjs();

            // Calculate the difference in hours
            const diffInHours = eventStartDateAndTime.diff(currentDateTime, 'hour');

            // Check if the difference is more than 24 hours
            const isMoreThanOneDay =
                diffInHours > 24 ? setIsDiffMoreThan24Hours(true) : setIsDiffMoreThan24Hours(false);

            // console.log('Is more than 24 hours until event start:', isMoreThanOneDay);
            return isMoreThanOneDay;
        }
        return false;
    }, [eventData?.start_date, eventData?.start_time]);

    function hasValueChanged(values, originalData) {
        const oldValues = {
            platformName: values.platformName,
            joiningLink: values.joiningLink,
            joiningCredentials: values.joiningCredentials,
            joiningInstructions: values.joiningInstructions
        };

        const cleanedOriginalData = {
            platformName: originalData?.platform_name || '',
            joiningLink: originalData?.online_location || '',
            joiningCredentials: originalData?.event_credentials || '',
            joiningInstructions: originalData?.event_joining_instructions || ''
        };

        return !isEqual(oldValues, cleanedOriginalData);
    }

    const initialValues = {
        platformName: eventData?.platform_name || '',
        joiningLink: eventData?.online_location || '',
        joiningCredentials: eventData?.event_credentials || '',
        joiningInstructions: eventData?.event_joining_instructions || ''
    };

    // handle the formik form
    const formik = useFormik({
        initialValues: initialValues,
        validationSchema: validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            try {
                setLoading(true);
                // reset the form values if platform is not decided
                if (values.platformName === PLATFORM_NOT_DECIDED) {
                    values.joiningLink = '';
                    values.joiningCredentials = '';
                    values.joiningInstructions = '';
                }

                const payload = {
                    step: 'location',
                    user_id: user?.id,
                    platform_name: values.platformName,
                    online_location: values.joiningLink,
                    event_credentials: values.joiningCredentials,
                    event_joining_instructions: values.joiningInstructions,
                    online_event: eventData?.online_event || true
                };

                // console.log("Online Location Payload -> ", payload)

                if (hasValueChanged(values, eventData)) {
                    await dispatch(
                        updateEventById({
                            eventId: eventData?.eventId || 70,
                            eventData: payload
                        })
                    ).unwrap();
                }

                // setOpenTicketPopup(true);
                if (tickets?.length === 0) {
                    setOpenTicketPopup(true);
                } else {
                    setOpenTicketPopup(false);
                }
            } catch (error) {
                errorToast(error?.data?.message || 'Failed to update event. Please try again.');
            } finally {
                setLoading(false);
            }
        }
    });

    function handleTicketRedirect() {
        setOpenTicketPopup(false);
        dispatch(selectMenu('tickets'));
        navigate('/event-dashboard/tickets');
    }

    function handleSkipForNow() {
        setOpenTicketPopup(false);
        dispatch(selectMenu('eventDashboard'));
        navigate('/event-dashboard/dashboard');
    }

    // return all the values, states and function
    return {
        loading,
        formik,
        platforms,
        toolbarOptions,
        isEditModeOn,
        setIsEditModeOn,
        openTicketPopup,
        setOpenTicketPopup,
        handleTicketRedirect,
        handleSkipForNow,
        isDiffMoreThan24Hours,
        shouldShowEditButton
    };
}

// validation schema
const validationSchema = Yup.object({
    platformName: Yup.string().required('Platform name selection is required'),
    joiningLink: Yup.string().when('platformName', {
        is: (val) => val !== PLATFORM_NOT_DECIDED,
        then: () => Yup.string().url().required('Joining link is required').label('Joining link'),
        otherwise: () => Yup.string()
    }),
    joiningCredentials: Yup.string(),
    joiningInstructions: Yup.string().when('platformName', {
        is: (val) => val !== PLATFORM_NOT_DECIDED,
        then: () => Yup.string().required('Joining instructions is required'),
        otherwise: () => Yup.string()
    })
});

// platforms data
const platforms = [
    {
        img: Zoom,
        platformName: 'Zoom'
    },
    {
        img: Meet,
        platformName: 'Meet'
    },
    {
        img: Youtube,
        platformName: 'Youtube'
    },
    {
        img: Discord,
        platformName: 'Discord'
    },
    {
        img: Twitch,
        platformName: 'Twitch'
    },
    {
        img: Link,
        platformName: 'Other'
    },
    {
        img: NotDecided,
        platformName: PLATFORM_NOT_DECIDED
    }
];

// Quill toolbar modules
const toolbarOptions = [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    ['blockquote', 'code-block'],
    ['link', 'image'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    [{ color: [] }, { background: [] }],
    [{ font: [] }],
    [{ align: [] }],
    ['clean'] // Clear the formatting button
];
